/**
 * Dropdown values and constants extracted from the BRD spec.
 * Single source of truth for all <Select> options used across PCF pages.
 */

import type {AudienceKey, StageId} from '../../../services/interface/pcfTypes';

export interface PcfSelectOption {
  value: string;
  label: string;
}

export const optionValues = (opts: PcfSelectOption[]): string[] =>
  opts.map((o) => o.value);

export const findLabel = (opts: PcfSelectOption[], value: string): string =>
  opts.find((o) => o.value === value)?.label ?? value;

// ===== Sub-Module 1: Goal & Scope =====

// SM1-F05a — Industry Sector (NIC-based; abbreviated list)
export const INDUSTRY_SECTORS: PcfSelectOption[] = [
  {
    value: 'agriculture_forestry_fishing',
    label: 'Agriculture, Forestry & Fishing'
  },
  {value: 'mining_quarrying', label: 'Mining & Quarrying'},
  {
    value: 'manufacturing_food_beverage',
    label: 'Manufacturing — Food & Beverage'
  },
  {
    value: 'manufacturing_textiles_apparel',
    label: 'Manufacturing — Textiles & Apparel'
  },
  {
    value: 'manufacturing_plastics_polymers',
    label: 'Manufacturing — Plastics & Polymers'
  },
  {
    value: 'manufacturing_chemicals_pharma',
    label: 'Manufacturing — Chemicals & Pharmaceuticals'
  },
  {
    value: 'manufacturing_cement_construction',
    label: 'Manufacturing — Cement & Construction Materials'
  },
  {
    value: 'manufacturing_iron_steel_metals',
    label: 'Manufacturing — Iron, Steel & Metals'
  },
  {
    value: 'manufacturing_machinery_equipment',
    label: 'Manufacturing — Machinery & Equipment'
  },
  {
    value: 'manufacturing_electronics_electrical',
    label: 'Manufacturing — Electronics & Electrical'
  },
  {
    value: 'manufacturing_automotive_transport',
    label: 'Manufacturing — Automotive & Transport Equipment'
  },
  {
    value: 'manufacturing_furniture_wood',
    label: 'Manufacturing — Furniture & Wood Products'
  },
  {value: 'manufacturing_paper_pulp', label: 'Manufacturing — Paper & Pulp'},
  {value: 'construction', label: 'Construction'},
  {value: 'electricity_gas_utilities', label: 'Electricity, Gas & Utilities'},
  {value: 'transportation_logistics', label: 'Transportation & Logistics'},
  {value: 'information_communication', label: 'Information & Communication'},
  {value: 'other_specify', label: 'Other (specify)'}
];

// SM1-F09a — Intended Application
export const INTENDED_APPLICATIONS: PcfSelectOption[] = [
  {value: 'internal_decision', label: 'Internal decision-making only'},
  {value: 'supplier_customer', label: 'Supplier / customer communication'},
  {value: 'public_marketing', label: 'Public marketing claim or green label'},
  {
    value: 'regulatory_cbam',
    label: 'Regulatory compliance (CBAM / CPR / ESPR)'
  },
  {value: 'epd_preparation', label: 'EPD preparation'},
  {value: 'carbon_neutrality', label: 'Carbon neutrality claim'},
  {value: 'investor_esg', label: 'Investor / ESG disclosure'},
  {value: 'academic_research', label: 'Academic / research purpose'}
];

export const CBAM_APPLICATION_VALUE = 'regulatory_cbam';

// Applications that trigger third-party verification warning
export const PUBLIC_FACING_APPLICATION_VALUES = [
  'public_marketing',
  'regulatory_cbam',
  'carbon_neutrality'
];

// SM1-F10 — PCF Standard / Methodology
export const PCF_STANDARDS: PcfSelectOption[] = [
  {
    value: 'iso_14067_2018',
    label: 'ISO 14067:2018 (Recommended — All-rounder)'
  },
  {
    value: 'ghg_protocol_product',
    label: 'GHG Protocol Product Standard (WRI/WBCSD 2011)'
  },
  {
    value: 'iso_14067_ghg_dual',
    label: 'ISO 14067 + GHG Protocol (Dual compliance)'
  },
  {value: 'en_15804_a2', label: 'EN 15804+A2:2019 (Construction products)'},
  {value: 'pef_pefcr', label: 'PEF / PEFCR (EU Environmental Footprint)'}
];

export const PCF_STANDARD_ISO_14067_VALUE = 'iso_14067_2018';
export const PCF_STANDARD_EN_15804_VALUE = 'en_15804_a2';
export const PCF_STANDARD_PEF_VALUE = 'pef_pefcr';

export const LCIA_EN_15804_VALUE = 'en_15804_a2_midpoint';
export const LCIA_EF_3_1_VALUE = 'ef_3_1';

// SM1-F11 — Intended Audience
export const INTENDED_AUDIENCES: Array<{value: AudienceKey; label: string}> = [
  {value: 'internal', label: 'Internal team only'},
  {value: 'management', label: 'Management / Board'},
  {value: 'customers', label: 'Customers / Buyers'},
  {value: 'regulatory', label: 'Regulatory authority'},
  {value: 'epd', label: 'EPD program operator'},
  {value: 'public', label: 'General public'},
  {value: 'financial', label: 'Financial institution'},
  {value: 'esg_investor', label: 'ESG investor'},
  {value: 'other', label: 'Others (Specify)'}
];

// SM1-F13 — Functional Unit measurement
export const FU_UNITS: PcfSelectOption[] = [
  {value: 'kg', label: 'kg'},
  {value: 'g', label: 'g'},
  {value: 'tonne', label: 'tonne'},
  {value: 'litre', label: 'litre'},
  {value: 'm3', label: 'm³'},
  {value: 'm2', label: 'm²'},
  {value: 'm_linear', label: 'm (linear metre)'},
  {value: 'unit_piece', label: 'unit / piece'},
  {value: 'kwh', label: 'kWh'},
  {value: 'mj', label: 'MJ'},
  {value: 'km', label: 'km'},
  {value: 'tonne_km', label: 'tonne-km'},
  {value: 'hour', label: 'hour'},
  {value: 'day', label: 'day'},
  {value: 'year', label: 'year'},
  {value: 'other_specify', label: 'other (specify)'}
];

// SM1-F16 — System Boundary Scope
export const SYSTEM_BOUNDARY_SCOPES: PcfSelectOption[] = [
  {value: 'cradle_to_gate', label: 'Cradle-to-Gate (A1–A3 only)'},
  {value: 'cradle_to_grave', label: 'Cradle-to-Grave (A1–C4)'},
  {
    value: 'gate_to_gate',
    label: 'Gate-to-Gate (A3 only — manufacturing step only)'
  }
];

export const STAGES_BY_BOUNDARY: Record<string, StageId[]> = {
  cradle_to_gate: ['A1', 'A2', 'A3'],
  cradle_to_grave: [
    'A1',
    'A2',
    'A3',
    'A4',
    'A5',
    'B1',
    'B2',
    'B3',
    'B4',
    'B5',
    'B6',
    'B7',
    'C1',
    'C2',
    'C3',
    'C4'
  ],
  gate_to_gate: ['A3']
};

// SM1-F19 — Geographic Scope
export const GEOGRAPHIC_SCOPES: PcfSelectOption[] = [
  {value: 'india_national', label: 'India (national)'},
  {value: 'india_north', label: 'India — North'},
  {value: 'india_south', label: 'India — South'},
  {value: 'india_east', label: 'India — East'},
  {value: 'india_west', label: 'India — West'},
  {value: 'global', label: 'Global'},
  {value: 'asia_pacific', label: 'Asia-Pacific'},
  {value: 'europe', label: 'Europe'},
  {value: 'north_america', label: 'North America'},
  {value: 'other', label: 'Other'}
];

// SM1-F20 — Allocation Method
export const ALLOCATION_METHODS: PcfSelectOption[] = [
  {value: 'physical_mass', label: 'Physical allocation — Mass (kg)'},
  {
    value: 'physical_volume',
    label: 'Physical allocation — Volume (litres / m³)'
  },
  {
    value: 'physical_energy',
    label: 'Physical allocation — Energy content (MJ)'
  },
  {
    value: 'economic_market_value',
    label: 'Economic allocation — Market value (INR or USD)'
  },
  {
    value: 'no_allocation',
    label: 'No allocation required (single-output product)'
  }
];

// SM1-F17 — Life Cycle Stages (with locked flag for Cradle-to-Gate)
export const LIFE_CYCLE_STAGES: Array<{value: StageId; label: string}> = [
  {value: 'A1', label: 'A1 Raw material extraction'},
  {value: 'A2', label: 'A2 Transport to factory'},
  {value: 'A3', label: 'A3 Manufacturing & processing'},
  {value: 'A4', label: 'A4 Distribution & transport to customer'},
  {value: 'A5', label: 'A5 Installation / construction'},
  {value: 'B1', label: 'B1 Use phase'},
  {value: 'B2', label: 'B2 Maintenance'},
  {value: 'B3', label: 'B3 Repair'},
  {value: 'B4', label: 'B4 Replacement'},
  {value: 'B5', label: 'B5 Refurbishment'},
  {value: 'B6', label: 'B6 Operational energy use'},
  {value: 'B7', label: 'B7 Operational water use'},
  {value: 'C1', label: 'C1 Deconstruction / de-manufacturing'},
  {value: 'C2', label: 'C2 Transport to waste'},
  {value: 'C3', label: 'C3 Waste processing'},
  {value: 'C4', label: 'C4 Disposal'},
  {value: 'D', label: 'D Beyond system boundary (net benefits/loads)'}
];

// ===== Sub-Module 3: Impact Configuration =====

// SM3-F01 — LCIA Method
export const LCIA_METHODS: PcfSelectOption[] = [
  {
    value: LCIA_EN_15804_VALUE,
    label:
      'EN 15804+A2:2019 — 19 mandatory impact indicators (construction EPD)'
  },
  {
    value: 'ipcc_2021_gwp100',
    label: 'IPCC 2021 GWP100 — Recommended (ISO 14067 compliant)'
  },
  {
    value: 'ipcc_2013_gwp100',
    label: 'IPCC 2013 GWP100 (AR5 — for legacy comparisons)'
  },
  {
    value: 'ef_3_1',
    label: 'EF 3.1 — Environmental Footprint (EU regulatory, 16 categories)'
  },
  {
    value: 'recipe_2016_midpoint',
    label: 'ReCiPe 2016 Midpoint (H) — Comprehensive multi-indicator'
  },
  {value: 'traci_2_1', label: 'TRACI 2.1 — US EPA (for US market EPDs)'},
  {
    value: 'cml_ia_baseline',
    label: 'CML-IA Baseline — European academic standard'
  }
];

export const GWP100_HORIZON_VALUE = 'gwp100';

// SM3-F02 — GWP Time Horizon
export const GWP_HORIZONS: PcfSelectOption[] = [
  {
    value: 'gwp100',
    label: '100 years (GWP100) — Standard and required by ISO 14067'
  },
  {value: 'gwp20', label: '20 years (GWP20) — Short-term forcing analysis'},
  {value: 'gwp500', label: '500 years (GWP500) — Long-term analysis'}
];

// SM3-F03 — Biogenic Carbon Handling
export const BIOGENIC_OPTIONS: PcfSelectOption[] = [
  {
    value: 'exclude_biogenic',
    label:
      'Exclude biogenic CO₂ (IPCC default — net zero assumption for short-cycle biogenic)'
  },
  {
    value: 'include_biogenic_separate',
    label:
      'Include biogenic CO2 uptake and emission separately (EN 15804+A2 approach; GWP-biogenic reported separately)'
  },
  {
    value: 'include_in_total',
    label:
      'Include in total GWP (conservative — biogenic treated same as fossil)'
  }
];

// ===== Sub-Module 5: Report & Export =====

// SM5-F01 — Report Type
export const REPORT_TYPES: PcfSelectOption[] = [
  {value: 'iso_14067_full', label: 'ISO 14067 Compliant PCF Report (full)'},
  {value: 'executive_summary', label: 'Executive Summary (1-page)'},
  {value: 'raw_data_export', label: 'Raw Data Export Only (no narrative)'},
  {
    value: 'internal_working',
    label:
      'Internal Working Document (with all data including Expert overrides)'
  }
];

// SM5-F02 — Report Language
export const REPORT_LANGUAGES: PcfSelectOption[] = [
  {value: 'english', label: 'English'},
  {value: 'hindi', label: 'Hindi'},
  {value: 'tamil', label: 'Tamil'},
  {value: 'telugu', label: 'Telugu'},
  {value: 'kannada', label: 'Kannada'},
  {value: 'marathi', label: 'Marathi'},
  {value: 'bengali', label: 'Bengali'},
  {value: 'gujarati', label: 'Gujarati'}
];

// SM5-F06 — Third-Party Verification Status
export const VERIFICATION_STATUSES: PcfSelectOption[] = [
  {value: 'not_verified', label: 'Not verified (internal use only)'},
  {value: 'verification_in_progress', label: 'Verification in progress'},
  {
    value: 'verified_body_required',
    label: 'Verified by third party — verification body name required'
  },
  {
    value: 'verified_certificate_attached',
    label: 'Verified by third party — see attached certificate'
  }
];

export const VERIFIED_THIRD_PARTY_VALUES = [
  'verified_body_required',
  'verified_certificate_attached'
];

// SM5-F08 — Export Formats
export const EXPORT_FORMATS: PcfSelectOption[] = [
  {value: 'pdf', label: 'PDF Report'},
  {value: 'csv', label: 'CSV (inventory data)'},
  {value: 'json', label: 'JSON (full study data — machine readable)'},
  {value: 'excel', label: 'Excel (.xlsx — inventory + results)'},
  {value: 'ilcd', label: 'ILCD format (for LCA software import — expert only)'}
];

export const PUBLISH_REGISTRY_VALUE = 'publish_registry';

// SM5-F09 — Share / Publish Study
export const SHARE_OPTIONS: PcfSelectOption[] = [
  {value: 'private_org', label: 'Private — accessible by my organisation only'},
  {
    value: 'share_specific_users',
    label: 'Share with specific users (enter email addresses)'
  },
  {
    value: PUBLISH_REGISTRY_VALUE,
    label: 'Publish to Bharat Carbon PCF Registry (public)'
  }
];

// ============================================================
// Sub-Module 2: LCI Data Collection (per FSD spec)
// ============================================================

// SM2-S1 Production Setup

// S1-F01 Manufacturing Setup
export const MANUFACTURING_SETUPS: PcfSelectOption[] = [
  {value: 'single_site', label: 'Single Site'},
  {value: 'multiple_sites', label: 'Multiple sites'},
  {value: 'contract_manufacturer', label: 'Contract manufacturer(s)'}
];

// S1-F02 Reference Period
export const REFERENCE_PERIODS: PcfSelectOption[] = [
  {value: 'monthly', label: 'Monthly'},
  {value: 'quarterly', label: 'Quarterly'},
  {value: 'annual', label: 'Annual'}
];

// S1-F05 Allocation Basis (also reused for A3-AF01)
export const ALLOCATION_BASES: PcfSelectOption[] = [
  {value: 'units_produced', label: 'Units produced (recommended)'},
  {value: 'machine_hours', label: 'Machine / production hours'},
  {value: 'floor_area', label: 'Floor area'},
  {value: 'economic_value', label: 'Economic value (revenue share)'},
  {value: 'manual_percent', label: 'Manual %'}
];

// S1-F04b Site location
export const INDIAN_STATES: PcfSelectOption[] = [
  {value: 'andhra_pradesh', label: 'Andhra Pradesh'},
  {value: 'assam', label: 'Assam'},
  {value: 'bihar', label: 'Bihar'},
  {value: 'chhattisgarh', label: 'Chhattisgarh'},
  {value: 'delhi', label: 'Delhi'},
  {value: 'gujarat', label: 'Gujarat'},
  {value: 'haryana', label: 'Haryana'},
  {value: 'himachal_pradesh', label: 'Himachal Pradesh'},
  {value: 'jharkhand', label: 'Jharkhand'},
  {value: 'karnataka', label: 'Karnataka'},
  {value: 'kerala', label: 'Kerala'},
  {value: 'madhya_pradesh', label: 'Madhya Pradesh'},
  {value: 'maharashtra', label: 'Maharashtra'},
  {value: 'odisha', label: 'Odisha'},
  {value: 'punjab', label: 'Punjab'},
  {value: 'rajasthan', label: 'Rajasthan'},
  {value: 'tamil_nadu', label: 'Tamil Nadu'},
  {value: 'telangana', label: 'Telangana'},
  {value: 'uttar_pradesh', label: 'Uttar Pradesh'},
  {value: 'uttarakhand', label: 'Uttarakhand'},
  {value: 'west_bengal', label: 'West Bengal'},
  {value: 'international', label: 'International'}
];

export const MANUFACTURING_SITES: PcfSelectOption[] = [
  {value: 'chennai_plant', label: 'Chennai Plant (Main Site)'},
  {value: 'pune_contract_mfg', label: 'Pune Contract Mfg'},
  {value: 'bangalore_site', label: 'Bangalore Site'}
];

// SM2-S2 A1 Bill of Materials

// A1-F03 Unit
export const MATERIAL_UNITS: PcfSelectOption[] = [
  {value: 'kg', label: 'kg'},
  {value: 'g', label: 'g'},
  {value: 'tonne', label: 'tonne'},
  {value: 'litre', label: 'litre'},
  {value: 'm3', label: 'm³'},
  {value: 'unit', label: 'unit'},
  {value: 'piece', label: 'piece'}
];

// A1-F04 Material Sourcing
export const MATERIAL_SOURCINGS: PcfSelectOption[] = [
  {value: 'india_national', label: 'India (National)'},
  {value: 'india_specific_state', label: 'India — Specific State'},
  {value: 'imported', label: 'Imported (specify country)'}
];

// A1-F05 Supplier EF
export const SUPPLIER_EF_OPTIONS: PcfSelectOption[] = [
  {value: 'yes_enter', label: 'Yes — I’ll enter it'},
  {value: 'no_database_default', label: 'No - Use database default'}
];

// A1-F07 Confidence / Data Quality (driver for DQ score)
export const CONFIDENCE_LEVELS: PcfSelectOption[] = [
  {value: 'weighed_measured', label: 'Weighed / measured directly'},
  {value: 'engineering_estimate', label: 'Engineering estimate'},
  {value: 'bom_system', label: 'Taken from BOM system'},
  {value: 'supplier_provided', label: 'Supplier provided'},
  {value: 'industry_average', label: 'Industry average'}
];

export const PACKAGING_OPTIONS: PcfSelectOption[] = [
  {value: 'primary', label: 'Primary (Plastic/Glass)'},
  {value: 'secondary', label: 'Secondary (Carton)'},
  {value: 'tertiary', label: 'Tertiary (Pallet/Wrap)'},
  {value: 'lubricants', label: 'Lubricants/Coolants'}
];

// SM2-S3 A2 Inbound Transport (also used for A4)

// A2-F04 Transport Mode
export const TRANSPORT_MODES: PcfSelectOption[] = [
  {value: 'road_truck_diesel', label: 'Road - Truck (Diesel)'},
  {value: 'road_truck_electric', label: 'Road - Truck (Electric)'},
  {value: 'rail', label: 'Rail'},
  {value: 'sea_container', label: 'Sea container'},
  {value: 'air', label: 'Air'},
  {value: 'mixed', label: 'Mixed (specify split)'}
];

// A2-F06 Truck Load Factor
export const TRUCK_LOAD_FACTORS: PcfSelectOption[] = [
  {value: 'ftl', label: 'Full Truck Load (FTL)'},
  {value: 'ltl', label: 'Less Than Truck Load (LTL)'},
  {value: 'unknown_default', label: 'I don’t know — use default 0.5'}
];

export const BOM_OPTIONS: PcfSelectOption[] = [
  {value: 'polypropylene_granules', label: 'Polypropylene granules'},
  {value: 'steel_bracket', label: 'Steel bracket'},
  {value: 'aluminium_sheet', label: 'Aluminium sheet'},
  {value: 'packaging_materials', label: 'Packaging materials'},
  {value: 'other', label: 'Other'}
];

// SM2-S4 A3 Manufacturing

// A3-E01 Energy Source / Type
export const ENERGY_TYPES: PcfSelectOption[] = [
  {value: 'grid_electricity', label: 'Grid Electricity'},
  {value: 'natural_gas', label: 'Natural Gas'},
  {value: 'lpg', label: 'LPG'},
  {value: 'diesel_generator', label: 'Diesel (generator)'},
  {value: 'furnace_oil', label: 'Furnace Oil'},
  {value: 'coal', label: 'Coal'},
  {value: 'biomass', label: 'Biomass'},
  {value: 'captive_solar', label: 'Captive Solar'},
  {value: 'captive_wind', label: 'Captive Wind'},
  {value: 'steam_purchased', label: 'Steam (purchased)'},
  {value: 'other', label: 'Other'}
];

// A3-E03 Energy Unit
export const ENERGY_UNITS: PcfSelectOption[] = [
  {value: 'kwh', label: 'kWh'},
  {value: 'mj', label: 'MJ'},
  {value: 'gj', label: 'GJ'},
  {value: 'm3', label: 'm³'},
  {value: 'kg', label: 'kg'},
  {value: 'litre', label: 'litre'}
];

// A3-P02 Process Emission Type
export const PROCESS_EMISSION_TYPES: PcfSelectOption[] = [
  {value: 'co2_fossil_process', label: 'CO₂ (fossil) — process release'},
  {value: 'co2_kiln_calcination', label: 'CO₂ from kiln / calcination'},
  {value: 'methane_leak', label: 'Methane leak'},
  {value: 'refrigerant_hfc_134a', label: 'Refrigerant — HFC-134a'},
  {value: 'refrigerant_hfc_410a', label: 'Refrigerant — HFC-410A'},
  {value: 'refrigerant_r22', label: 'Refrigerant — R-22'},
  {value: 'sf6_switchgear', label: 'SF₆ (electrical switchgear)'},
  {value: 'n2o_process', label: 'N₂O (process)'},
  {value: 'other_ghg', label: 'Other GHG'}
];

// SM2-S5 Waste

// A3-W02 Waste Type
export const WASTE_TYPES: PcfSelectOption[] = [
  {
    value: 'solid_non_hazardous',
    label: 'Solid non-hazardous (plastic trim, metal scrap, rejects)'
  },
  {value: 'hazardous_incinerated', label: 'Hazardous waste — incinerated'},
  {
    value: 'hazardous_solvents',
    label: 'Hazardous waste — solvents / chemicals'
  },
  {value: 'wastewater', label: 'Wastewater'},
  {
    value: 'packaging_waste_incoming',
    label: 'Packaging waste from incoming materials'
  }
];

// A3-W04 Waste Treatment / Destination
export const WASTE_TREATMENTS: PcfSelectOption[] = [
  {value: 'landfilled', label: 'Landfilled'},
  {value: 'incinerated', label: 'Incinerated'},
  {value: 'recycled', label: 'Recycled'},
  {value: 'sold_scrap', label: 'Sold as scrap'},
  {value: 'wastewater_onsite', label: 'Wastewater — on-site treatment'},
  {
    value: 'wastewater_municipal',
    label: 'Wastewater — sent to municipal treatment'
  },
  {value: 'hazardous_contractor', label: 'Hazardous waste contractor'}
];

// Water Stress Region
export const WATER_STRESS_REGIONS: PcfSelectOption[] = [
  {value: 'indus_basin', label: 'Indus Basin'},
  {value: 'ganges_basin', label: 'Ganges Basin'},
  {value: 'brahmaputra_basin', label: 'Brahmaputra Basin'},
  {value: 'godavari_basin', label: 'Godavari Basin'},
  {value: 'krishna_basin', label: 'Krishna Basin'},
  {value: 'cauvery_basin', label: 'Cauvery Basin'},
  {value: 'narmada_basin', label: 'Narmada Basin'},
  {value: 'coastal_high_stress', label: 'Coastal — high stress'},
  {value: 'coastal_low_stress', label: 'Coastal — low stress'},
  {value: 'other', label: 'Other'}
];

// SM2-S6 Outbound

// B-F01 sub-stage
export const B_SUB_STAGES: PcfSelectOption[] = [
  {value: 'b1_use', label: 'B1 Use'},
  {value: 'b2_maintenance', label: 'B2 Maintenance'},
  {value: 'b3_repair', label: 'B3 Repair'},
  {value: 'b4_replacement', label: 'B4 Replacement'},
  {value: 'b5_refurbishment', label: 'B5 Refurbishment'},
  {value: 'b6_operational_energy', label: 'B6 Operational energy use'},
  {value: 'b7_operational_water', label: 'B7 Operational water use'}
];

// B-F02 unit
export const B_UNITS: PcfSelectOption[] = [
  {value: 'kwh_per_year', label: 'kWh / year'},
  {value: 'litres_per_year', label: 'litres / year'},
  {value: 'kg_per_year', label: 'kg / year'},
  {value: 'hours_per_year', label: 'hours / year'},
  {value: 'cycles_per_year', label: 'cycles / year'},
  {value: 'unit_per_year', label: 'unit / year'}
];

// C-F01 sub-stage
export const C_SUB_STAGES: PcfSelectOption[] = [
  {value: 'c1_deconstruction', label: 'C1 Deconstruction'},
  {value: 'c2_transport_waste', label: 'C2 Transport to waste'},
  {value: 'c3_waste_processing', label: 'C3 Waste processing'},
  {value: 'c4_disposal', label: 'C4 Disposal'}
];

export const C_UNITS: PcfSelectOption[] = [
  {value: 'kg_per_unit', label: 'kg / unit'},
  {value: 'tonnes_per_year', label: 'tonnes / year'},
  {value: 'unit_per_year', label: 'unit / year'},
  {value: 'percent_mass', label: '% of mass'}
];

export const PRODUCTION_TIME_UNITS: PcfSelectOption[] = [
  {value: 'hours', label: 'Hours'},
  {value: 'days', label: 'Production days'}
];

export const SHIP_TO_OPTIONS: PcfSelectOption[] = [
  {value: 'customers', label: 'Directly to customers'},
  {value: 'distribution_centres', label: 'To distribution centres'},
  {value: 'both', label: 'Both'}
];

export const HAS_PROCESS_EMISSIONS_OPTIONS: PcfSelectOption[] = [
  {value: 'yes', label: 'Yes'},
  {value: 'no', label: 'No'},
  {value: 'not_sure', label: 'Not sure'}
];

export const PROCESS_ALLOCATION_SCOPE_OPTIONS: PcfSelectOption[] = [
  {value: 'product_specific', label: 'Specific to this product'},
  {
    value: 'whole_facility_units',
    label: 'Whole facility — allocate by units produced'
  },
  {
    value: 'whole_facility_time',
    label: 'Whole facility — allocate by production time'
  }
];

export const WASTE_SCOPE_OPTIONS: PcfSelectOption[] = [
  {value: 'product_specific', label: 'Specific to this product'},
  {
    value: 'whole_facility_units',
    label: 'Whole facility — allocate by units produced'
  }
];

export const USES_ENERGY_IN_USE_OPTIONS: PcfSelectOption[] = [
  {value: 'electricity', label: 'Yes — electricity'},
  {value: 'fuel', label: 'Yes — fuel'},
  {value: 'both', label: 'Yes — both'},
  {value: 'no', label: 'No'}
];

export const B_ENERGY_TYPE_OPTIONS: PcfSelectOption[] = [
  {value: 'electricity', label: 'Electricity'},
  {value: 'fuel', label: 'Fuel'},
  {value: 'both', label: 'Both'}
];

export const SERVICE_LIFE_UNITS: PcfSelectOption[] = [
  {value: 'years', label: 'Years'},
  {value: 'months', label: 'Months'}
];

export const YES_NO_OPTIONS: PcfSelectOption[] = [
  {value: 'yes', label: 'Yes'},
  {value: 'no', label: 'No'}
];

export const DISPOSAL_ROUTE_OPTIONS: PcfSelectOption[] = [
  {value: 'landfill', label: 'Goes to landfill'},
  {value: 'recycled', label: 'Recycled — material recovery'},
  {value: 'incinerated', label: 'Incinerated with energy recovery'},
  {value: 'returned_manufacturer', label: 'Returned to manufacturer'},
  {value: 'mix', label: 'Mix of the above'}
];

export const DISPOSAL_TRANSPORT_OPTIONS: PcfSelectOption[] = [
  {value: 'nearby', label: 'Yes — to a nearby facility (< 50 km)'},
  {value: 'distant', label: 'Yes — to a distant facility (50+ km)'},
  {value: 'customer_premises', label: 'Typically collected from customer premises'},
  {value: 'unknown', label: 'Unknown'}
];

export const RECOVERABLE_VALUE_OPTIONS: PcfSelectOption[] = [
  {value: 'recyclable_content', label: 'Yes — recyclable content'},
  {value: 'energy_recovery', label: 'Yes — energy recovery'},
  {value: 'no', label: 'No'},
  {value: 'unknown', label: 'Unknown'}
];

export const WASTE_CHECKLIST_OPTIONS: PcfSelectOption[] = WASTE_TYPES;
